import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import { Injectable, inject, resource, computed } from "@angular/core";
import { firstValueFrom, filter, take } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { SKIP_AUTH_HEADERS } from "../auth/auth.context";

export type AppSettings = Record<string, string | undefined>;
@Injectable({ providedIn: "root" })
export class AppSettingsService {
    private http = inject(HttpClient);

    private appSettingsCached: AppSettings | null = null;

    protected appSettingsResource = resource<AppSettings | null, undefined>({
        defaultValue: null,
        loader: () => this.loadSettings(),
    });
    public appSettings = computed(() => this.appSettingsResource.value());
    private readonly appSettings$ = toObservable(this.appSettings);

    private async loadSettings(): Promise<AppSettings> {
        if (!this.appSettingsCached) {
            let url = "/app-settings/index.js";
            const options = {
                // App settings are a bootstrap dependency of AuthService, so they cannot be authenticated.
                context: new HttpContext().set(SKIP_AUTH_HEADERS, true),
                params: new HttpParams().set("t", Date.now().toString()),
            };
            if (
                /localhost/.test(document.location.host) ||
                /taxprepdashboard.dev-local.ca/.test(document.location.host) ||
                /dev-local.taxprepdashboard.ca/.test(document.location.host)
            ) {
                url = "/app-settings/dev-local.json";
            }
            try {
                const data = this.http.get<AppSettings>(url, options);
                this.appSettingsCached = await firstValueFrom(data);
            } catch (error) {
                console.error("[WK] Failed to load app settings:", error);
                this.appSettingsCached = {};
            }
        }
        return this.appSettingsCached;
    }

    public async getAppSettings(): Promise<AppSettings | null> {
        await this.waitForAppSettings();
        return this.appSettingsCached;
    }

    public getSetting(key: string): string {
        return this.appSettingsCached?.[key] ?? "";
    }

    // Do not use it. Only use in getLoadSettings() for fallback compatibility. It will be removed in the future.
    private async waitForAppSettings(): Promise<void> {
        await firstValueFrom(
            this.appSettings$.pipe(
                filter((appSettings) => appSettings !== undefined && appSettings !== null),
                take(1),
            ),
        );
    }
}
